import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as yup from "yup";

import { ILogin } from "../../../../../@types/Login";
import { IUserSurveyRequest } from "../../../../../@types/Surveys";
import { ReactComponent as PromsEmptyState } from "../../../../../assets/images/proms-empty-state.svg";
import { Button } from "../../../../../components/Button";
import { createColumn, Table } from "../../../../../components/Table";
import { TextField } from "../../../../../components/TextField";
import { PageTitle } from "../../../../../layout/components/PageTitle";
import { authService } from "../../../../../services/authService";
import { surveysService } from "../../../../../services/surveysService";
import {
  ErrorMessage,
  inputCss,
  InputsContainer,
  ListContainer,
  loginButtonStyles,
  LoginContainer,
  MainContainer,
} from "./AssignedSurveysList.styles";

export function AssignedSurveysList() {
  const { t } = useTranslation();

  const [capsLockEnabled, setCapsLockEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [patientUserId, setPatientUserId] = useState<number | undefined>();
  // EN-5289: the patient's token stays here and in the survey URL. Storing it as
  // tokenResponse (with isMobileApp) replaced the staff session and left every
  // page behind DashboardLayout's mobile spinner.
  const [patientToken, setPatientToken] = useState<string | undefined>();

  const [assignedSurveys, setAssignedSurveys] = useState<IUserSurveyRequest[]>(
    []
  );
  const navigate = useNavigate();

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("login_validation_emailInvalid"))
      .required(t("login_validation_emailRequired")),
    password: yup.string().required(t("login_validation_passwordRequired")),
  });

  const handleOnSubmitForm = async ({ email, password }: ILogin) => {
    setIsLoading(true);

    const fetchedMobileToken = await authService.getMobileIdToken({
      email,
      password,
    });

    const {
      errors,
      idToken,
      patientUserId: fetchedPatientUserId,
    } = fetchedMobileToken;

    if (errors) {
      setIsLoading(false);
      setGlobalError(errors[0].message);
      return;
    }

    setPatientUserId(fetchedPatientUserId);
    setPatientToken(idToken);

    const fetchedAssignedSurveys = (
      await surveysService.getAssignedSurveysMobile(idToken)
    ).data;

    if (fetchedAssignedSurveys) {
      setAssignedSurveys(fetchedAssignedSurveys);
    }

    setIsLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const handleOnKeyUp = (event: any) => {
    const capsEnabled =
      (event.getModifierState && event.getModifierState("CapsLock")) || false;

    setCapsLockEnabled(capsEnabled);

    if (event.key === "Enter") {
      formik.submitForm();
    }
  };

  const hasData = assignedSurveys?.length > 0;

  const surveyTableColumns = [
    createColumn({
      keyName: "surveyName",
      header: () => t("surveys_patientLogin_columns_name"),
      cell: (info) => info.renderValue(),
      size: 80,
    }),
    createColumn({
      keyName: "surveyId",
      header: () => t("surveys_patientLogin_columns_surveyId"),
      cell: (info) => info.renderValue(),
      size: 80,
    }),
    createColumn({
      keyName: "id",
      header: () => t("surveys_patientLogin_columns_userSurveyId"),
      cell: (info) => info.renderValue(),
      size: 80,
    }),
    createColumn({
      keyName: "createdBy",
      header: () => t("surveys_patientLogin_columns_createdBy"),
      cell: (info) => info.renderValue(),
      size: 20,
    }),
    createColumn({
      keyName: "updatedBy",
      header: () => t("surveys_patientLogin_columns_lastUpdatedBy"),
      cell: (info) => info.renderValue(),
      size: 20,
    }),
  ];

  const handleOnRowClick = (rowData: IUserSurveyRequest) => {
    const params = new URLSearchParams();
    if (patientUserId) {
      params.set("patientUserId", patientUserId.toString());
    }
    if (patientToken) {
      params.set("token", patientToken);
    }
    navigate(`/surveys/answer/${rowData.id}?${params.toString()}`);
  };

  return (
    <MainContainer>
      <LoginContainer>
        <PageTitle>{t("surveys_patientLogin_title")}</PageTitle>
        <InputsContainer>
          <TextField
            name="email"
            label={t("surveys_patientLogin_emailLabel")}
            value={formik.values.email}
            onChange={formik.handleChange}
            placeholder="patient@example.com"
            className={inputCss()}
          />
          <TextField
            isPassword
            name="password"
            label={t("surveys_patientLogin_passwordLabel")}
            value={formik.values.password}
            onChange={formik.handleChange}
            placeholder="●●●●●●●●●●●●"
            warningMessage={capsLockEnabled ? t("login_capsLockOn") : undefined}
            onKeyDown={handleOnKeyUp}
            className={inputCss()}
          />
          <Button
            type="button"
            label={
              !hasData
                ? t("surveys_patientLogin_submit")
                : t("surveys_patientLogin_changePatient")
            }
            isLoading={isLoading}
            className={loginButtonStyles()}
            onClick={formik.submitForm}
          />
        </InputsContainer>
        {globalError && <ErrorMessage>{globalError}</ErrorMessage>}
      </LoginContainer>

      <ListContainer>
        {hasData ? (
          <Table
            data={assignedSurveys}
            emptyStateText="no data"
            columns={surveyTableColumns}
            onRowClick={handleOnRowClick}
          />
        ) : (
          <>
            <PromsEmptyState />
            <p style={{ marginTop: "20px" }}> {t("common_noDataToShow")}</p>
          </>
        )}
      </ListContainer>
    </MainContainer>
  );
}
